import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getUserProfileApi, updateUserApi, markUsersSeenApi } from '../api/users.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import { useNavBadgeContext } from '../context/NavBadgeContext';
import PageHeader from '../components/PageHeader';
import UserProfileBilling from '../components/UserProfileBilling';
import PlanCell from '../components/users/PlanCell';
import { formatRelativeDate } from '../utils/userDirectoryFilters';

function ReadField({ label, value, mono }) {
  return (
    <div>
      <div className="profile-field-label">{label}</div>
      <div
        className="profile-field-readonly"
        style={mono ? { fontFamily: "'DM Mono',monospace", fontSize: 12 } : undefined}
      >
        {value || '—'}
      </div>
    </div>
  );
}

function StatusBadge({ label, tone = 'gray' }) {
  const tones = {
    green: { bg: '#DCFCE7', color: '#15803D' },
    blue: { bg: '#DBEAFE', color: '#1D4ED8' },
    amber: { bg: '#FEF3C7', color: '#B45309' },
    gray: { bg: '#F1F5F9', color: '#475569' },
  };
  const t = tones[tone] || tones.gray;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        background: t.bg,
        color: t.color,
        padding: '3px 9px',
        borderRadius: 99,
      }}
    >
      {label}
    </span>
  );
}

function verificationTone(status) {
  if (status === 'fully_verified' || status === 'Fully verified') return 'green';
  if (status === 'email_verified' || status === 'Email verified') return 'blue';
  if (status === 'mobile_verified' || status === 'Phone verified') return 'blue';
  return 'amber';
}

function verificationLabel(status) {
  const map = {
    fully_verified: 'Fully verified',
    email_verified: 'Email verified',
    mobile_verified: 'Mobile verified',
    unverified: 'Unverified',
  };
  return map[status] || status || 'Unverified';
}

function inviteStatusTone(status) {
  if (status === 'accepted') return 'green';
  if (status === 'invited' || status === 'pending') return 'amber';
  return 'gray';
}

export default function UserProfile() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { refreshBadges } = useNavBadgeContext();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');

  const userInfo = user?.userInfo || {};
  const enrollment = user?.enrollment || {};
  const flagsAndNotes = user?.flagsAndNotes || {};
  const directory = user?.directory || {};
  const trustedCircle = user?.trustedCircle;
  const profileExt = userInfo?.profile;

  const displayName =
    `${userInfo?.firstName || ''} ${userInfo?.lastName || ''}`.trim() ||
    userInfo?.email ||
    'Profile';

  const activityRows =
    flagsAndNotes?.recentActivity?.map((row) => ({
      id: row.id || row.event,
      event: row.event,
      date: row.date,
      status: row.status,
      statusColor:
        row.status === 'OK'
          ? '#2E7D32'
          : row.status === 'Failed'
            ? '#B91C1C'
            : '#B45309',
    })) || [];

  async function fetchUserProfile() {
    setLoading(true);
    const result = await apiHandler(() => getUserProfileApi(userId));
    if (result.success) {
      setUser(unwrapData(result));
    } else {
      setUser(null);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchUserProfile();
  }, [userId]);

  useEffect(() => {
    async function markSeen() {
      await apiHandler(() => markUsersSeenApi());
      refreshBadges();
    }
    markSeen();
  }, [refreshBadges]);

  useEffect(() => {
    if (flagsAndNotes?.staffNotes != null) {
      setNotes(flagsAndNotes.staffNotes);
    }
  }, [flagsAndNotes?.staffNotes]);

  async function saveProfile() {
    const result = await apiHandler(() =>
      updateUserApi(userId, { staffNotes: notes })
    );
    if (result.success) await fetchUserProfile();
    return result;
  }

  if (loading) {
    return <p style={{ padding: 24, color: '#64748B' }}>Loading user profile…</p>;
  }

  if (!user) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: '#B91C1C', marginBottom: 12 }}>User not found.</p>
        <button type="button" className="action-btn btn-blue" onClick={() => navigate('/users')}>
          Back to users
        </button>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        inline
        trail={[
          { label: 'Users', to: '/users' },
          { label: displayName },
        ]}
        actions={
          <>
            <button
              type="button"
              className="action-btn"
              style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-arrow-left" style={{ fontSize: 12 }} />
              Back
            </button>
            <button
              type="button"
              className="action-btn btn-orange"
              onClick={async () => {
                const result = await saveProfile();
                if (!result.success) alert(result.message || 'Save failed');
              }}
            >
              <i className="fas fa-floppy-disk" style={{ fontSize: 12 }} />
              Save
            </button>
          </>
        }
      />

      {/* Summary strip */}
      <div
        className="fade-up"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 16,
          padding: '14px 18px',
          background: '#fff',
          border: '1px solid #E8EDF5',
          borderRadius: 14,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: 'linear-gradient(135deg,#102A43,#074f78)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          {`${userInfo?.firstName?.[0] || ''}${userInfo?.lastName?.[0] || ''}` || '??'}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0F172A' }}>{displayName}</div>
          <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
            {userInfo?.email || '—'}
            {userInfo?.mobile ? ` · ${userInfo.mobile}` : ''}
          </div>
        </div>
        <StatusBadge
          label={directory.onboarding?.complete ? 'Onboarding complete' : 'Onboarding in progress'}
          tone={directory.onboarding?.complete ? 'green' : 'amber'}
        />
        <StatusBadge
          label={verificationLabel(userInfo.verificationStatus)}
          tone={verificationTone(userInfo.verificationStatus)}
        />
        <StatusBadge label={`Account: ${userInfo.status || 'pending'}`} tone="gray" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch">
        {/* Account & contact */}
        <div className="fade-up delay-1">
          <div className="profile-section-card">
            <div className="profile-section-title">
              <i className="fas fa-id-card" style={{ color: '#ec7426', fontSize: 14, marginRight: 6 }} />
              Account & contact
            </div>
            <div style={{ display: 'grid', gap: 14 }}>
              <ReadField label="First name" value={userInfo.firstName} />
              <ReadField label="Last name" value={userInfo.lastName} />
              <ReadField label="Email" value={userInfo.email} />
              <ReadField
                label="Mobile"
                value={
                  userInfo.mobile
                    ? `${userInfo.countryCode && !String(userInfo.mobile).startsWith('+') ? `${userInfo.countryCode} ` : ''}${userInfo.mobile}`
                    : null
                }
                mono
              />
              <ReadField label="City" value={userInfo.city} />
              <ReadField label="State" value={userInfo.state} />
              <ReadField label="Zip code" value={userInfo.zipCode} />
              <ReadField label="Timezone" value={userInfo.timezone} />
              <ReadField label="Auth provider" value={userInfo.authProvider} />
              <ReadField label="Unique ID" value={userInfo.displayId || userInfo.id} mono />
              <ReadField label="Signed up" value={formatRelativeDate(userInfo.createdAt)} />
              <ReadField
                label="Last login"
                value={directory.activity?.label === 'Never' ? 'Never' : `${directory.activity?.label}${directory.activity?.label !== 'Today' ? ' ago' : ''}`}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <StatusBadge
                  label={userInfo.emailVerified ? 'Email verified' : 'Email unverified'}
                  tone={userInfo.emailVerified ? 'green' : 'amber'}
                />
                <StatusBadge
                  label={userInfo.phoneVerified ? 'Phone verified' : 'Phone unverified'}
                  tone={userInfo.phoneVerified ? 'green' : 'amber'}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Onboarding & plan */}
        <div className="fade-up delay-2">
          <div className="profile-section-card" style={{ marginBottom: 20 }}>
            <div className="profile-section-title">
              <i className="fas fa-list-check" style={{ color: '#074f78', fontSize: 14, marginRight: 6 }} />
              Onboarding
            </div>
            <div style={{ display: 'grid', gap: 14 }}>
              <ReadField
                label="Status"
                value={directory.onboarding?.complete ? 'Complete' : 'In progress'}
              />
              {!directory.onboarding?.complete && (
                <>
                  <ReadField label="Current step" value={directory.onboarding?.currentStepLabel} />
                  <ReadField label="Progress" value={directory.onboarding?.progressLabel} />
                </>
              )}
              <ReadField
                label="Completed at"
                value={
                  directory.onboarding?.completedAt
                    ? formatRelativeDate(directory.onboarding.completedAt)
                    : '—'
                }
              />
            </div>
          </div>

          <div className="profile-section-card">
            <div className="profile-section-title">
              <i className="fas fa-credit-card" style={{ color: '#074f78', fontSize: 14, marginRight: 6 }} />
              Plan & billing
            </div>
            <div style={{ marginBottom: 14 }}>
              <PlanCell plan={directory.plan} />
            </div>
            <div style={{ display: 'grid', gap: 14 }}>
              <ReadField label="Billing status" value={enrollment.billingStatus} />
              <ReadField label="Price" value={enrollment.priceLabel} />
              <ReadField label="Enrollment date" value={enrollment.enrollmentDate} />
              <ReadField label="Renewal" value={enrollment.renewalLabel || enrollment.renewalDate} />
              <ReadField label="Trial ends" value={enrollment.trialEndsAt} />
              {enrollment.cancelAtPeriodEnd && (
                <ReadField label="Cancellation" value="Cancels at period end" />
              )}
              <ReadField label="Stripe customer" value={enrollment.stripeCustomerId} mono />
            </div>
          </div>
        </div>

        {/* Flags & notes */}
        <div className="fade-up delay-3">
          <div className="profile-section-card">
            <div className="profile-section-title">
              <i className="fas fa-flag" style={{ color: '#c4892a', fontSize: 14, marginRight: 6 }} />
              Flags & notes
            </div>
            <div style={{ marginBottom: 16 }}>
              <div className="profile-field-label">Flags</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {(flagsAndNotes.flags?.length ? flagsAndNotes.flags : ['No flags']).map((flag) => (
                  <span key={flag} className="flag-tag">
                    <i className="fas fa-check-circle" /> {flag}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div className="profile-field-label">Staff notes</div>
              <textarea
                className="staff-notes-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            {flagsAndNotes.responderNotes && (
              <ReadField label="Responder notes" value={flagsAndNotes.responderNotes} />
            )}
          </div>
        </div>
      </div>

      {/* Trusted circle */}
      <div className="fade-up delay-2" style={{ marginTop: 20 }}>
        <div className="profile-section-card">
          <div
            className="profile-section-title"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span>
              <i className="fas fa-circle-nodes" style={{ color: '#C2410C', fontSize: 14, marginRight: 6 }} />
              Trusted circle
            </span>
            {trustedCircle?.id && (
              <Link
                to={`/trusted-circles/${trustedCircle.id}`}
                className="action-btn btn-blue"
                style={{ padding: '6px 12px', fontSize: 12, textDecoration: 'none' }}
              >
                Open circle
              </Link>
            )}
          </div>
          {trustedCircle ? (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <ReadField label="Circle ID" value={trustedCircle.circleId} mono />
                <ReadField label="Status" value={trustedCircle.status} />
                <ReadField label="State" value={trustedCircle.state} />
                <ReadField
                  label="Contacts"
                  value={directory.trustedContacts?.label || `${trustedCircle.memberCount} contacts`}
                />
              </div>
              {trustedCircle.members?.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ minWidth: 700 }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Relationship</th>
                        <th>Status</th>
                        <th>Added</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trustedCircle.members.map((m) => (
                        <tr key={m.id}>
                          <td style={{ fontWeight: 600, fontSize: 13 }}>
                            {m.name}
                            {m.isEmergencyContact && (
                              <span style={{ fontSize: 10, color: '#C2410C', marginLeft: 6 }}>Emergency</span>
                            )}
                          </td>
                          <td style={{ fontSize: 12, fontFamily: "'DM Mono',monospace" }}>{m.phone || '—'}</td>
                          <td style={{ fontSize: 12, color: '#64748B' }}>{m.email || '—'}</td>
                          <td style={{ fontSize: 12, color: '#64748B' }}>{m.relationship || '—'}</td>
                          <td>
                            <StatusBadge label={m.inviteStatus} tone={inviteStatusTone(m.inviteStatus)} />
                          </td>
                          <td style={{ fontSize: 12, color: '#94A3B8' }}>{formatRelativeDate(m.addedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: '#64748B', fontSize: 13 }}>No trusted contacts added yet.</p>
              )}
            </>
          ) : (
            <p style={{ color: '#64748B', fontSize: 13 }}>User has not created a trusted circle.</p>
          )}
        </div>
      </div>

      {/* Extended profile / medical */}
      {profileExt && (
        <div className="fade-up delay-3" style={{ marginTop: 20 }}>
          <div className="profile-section-card">
            <div className="profile-section-title">
              <i className="fas fa-heart-pulse" style={{ color: '#BE123C', fontSize: 14, marginRight: 6 }} />
              Profile & medical
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ReadField label="Date of birth" value={profileExt.dateOfBirth || userInfo.dateOfBirth} />
              <ReadField label="Sex" value={profileExt.sex || userInfo.gender} />
              <ReadField label="Blood type" value={profileExt.bloodType} />
              <ReadField label="Height (cm)" value={profileExt.heightCm} />
              <ReadField label="Hair color" value={profileExt.hairColor} />
              <ReadField label="Primary language" value={profileExt.primaryLanguage} />
              <ReadField label="Allergies" value={profileExt.allergies} />
              <ReadField label="Medications" value={profileExt.medications} />
              <ReadField label="Medical conditions" value={profileExt.medicalConditions} />
              <ReadField label="Emergency contact" value={profileExt.emergencyContactName} />
              <ReadField label="Emergency phone" value={profileExt.emergencyContactPhone} mono />
              <ReadField label="Preferred hospital" value={profileExt.preferredHospital} />
              <ReadField label="Vehicle info" value={profileExt.vehicleInfo} />
              <ReadField label="Distinguishing features" value={profileExt.distinguishingFeatures} />
            </div>
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div className="fade-up delay-3" style={{ marginTop: 20, marginBottom: 20 }}>
        <div className="profile-section-card">
          <div className="profile-section-title">
            <i className="fas fa-clock-rotate-left" style={{ color: '#475569', fontSize: 14, marginRight: 6 }} />
            Recent activity
          </div>
          {activityRows.length > 0 ? (
            <div style={{ border: '1px solid #F1F5F9', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }} className="activity-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Event</th>
                    <th style={{ textAlign: 'left' }}>Date</th>
                    <th style={{ textAlign: 'left' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {activityRows.map((row) => (
                    <tr key={row.id}>
                      <td style={{ fontWeight: 500 }}>{row.event}</td>
                      <td style={{ color: '#64748B' }}>{row.date}</td>
                      <td>
                        <span style={{ color: row.statusColor, fontSize: 11, fontWeight: 600 }}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#64748B', fontSize: 13 }}>No recent activity recorded.</p>
          )}
        </div>
      </div>

      <UserProfileBilling userId={userId} />
    </>
  );
}
